import { useState } from 'react';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../shared/auth/useAuth';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { downloadAsPdf } from '../../../shared/reports/downloadAsPdf';
import { downloadAsXlsx } from '../../../shared/reports/downloadAsXlsx';
import { buildFilename, formatDate, formatMoney } from '../../../shared/reports/formatters';
import { useGerarAdvances } from '../hooks';
import { RelatorioPageShell } from '../RelatorioPageShell';
import { PeriodoFilter, defaultPeriodoMes } from '../components/PeriodoFilter';
import type { AdvancesDto, ValeStatus } from '../types';

const STATUS_LABELS: Record<ValeStatus, string> = {
  ABE: 'Aberto',
  PAG: 'Pago',
  CAN: 'Cancelado',
};

export function ValesPage(): JSX.Element {
  const [periodo, setPeriodo] = useState(defaultPeriodoMes());
  const [empregadoId, setEmpregadoId] = useState<number | null>(null);
  const mutation = useGerarAdvances();
  const { user } = useAuth();
  const usuario = user?.nome ?? '';

  const result = mutation.data;
  const canGenerate = !!periodo.dataInicio && !!periodo.dataFim;

  function gerar(): void {
    mutation.mutate({
      dataInicio: periodo.dataInicio,
      dataFim: periodo.dataFim,
      ...(empregadoId !== null ? { empregadoId } : {}),
    });
  }

  function exportPdf(): void {
    if (!result) return;
    downloadAsPdf(
      buildFilename('vales', periodo.dataInicio, periodo.dataFim),
      {
        title: 'Vales / Adiantamentos',
        subtitle: `${formatDate(periodo.dataInicio)} a ${formatDate(periodo.dataFim)}`,
        usuario,
      },
      ({ doc, contentStartY }) => {
        autoTable(doc, {
          startY: contentStartY,
          head: [['Colaborador', 'Solicitação', 'Valor', 'Parcelas', '1ª Parcela', 'Status']],
          body: result.linhas.map((l) => [
            l.colaboradorNome,
            formatDate(l.dataSolicitacao),
            formatMoney(l.valor),
            String(l.nroParcelas),
            formatDate(l.vencimento1aParcela),
            STATUS_LABELS[l.status],
          ]),
          foot: [
            [
              {
                content: `Total: ${result.totais.count} vales`,
                colSpan: 2,
                styles: { halign: 'right', fontStyle: 'bold' },
              },
              formatMoney(result.totais.totalValor),
              '',
              '',
              '',
            ],
          ],
          styles: { fontSize: 9 },
          headStyles: { fillColor: [30, 60, 100] },
          footStyles: { fillColor: [240, 240, 240], textColor: 20, fontStyle: 'bold' },
        });

        if (result.totais.porStatus.length > 0) {
          autoTable(doc, {
            head: [['Status', 'Count', 'Valor']],
            body: result.totais.porStatus.map((s) => [
              STATUS_LABELS[s.status as ValeStatus] ?? s.status,
              String(s.count),
              formatMoney(s.valor),
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [80, 80, 80] },
          });
        }
      },
    );
  }

  function exportXlsx(): void {
    if (!result) return;
    downloadAsXlsx(buildFilename('vales', periodo.dataInicio, periodo.dataFim), [
      {
        name: 'Vales',
        rows: result.linhas.map((l) => ({
          Colaborador: l.colaboradorNome,
          Solicitacao: l.dataSolicitacao,
          Valor: l.valor,
          Parcelas: l.nroParcelas,
          PrimeiraParcela: l.vencimento1aParcela,
          Status: STATUS_LABELS[l.status],
        })),
        columns: [
          { key: 'Colaborador', header: 'Colaborador', width: 28 },
          { key: 'Solicitacao', header: 'Solicitação', format: 'date', width: 14 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
          { key: 'Parcelas', header: 'Parcelas', format: 'integer', width: 10 },
          { key: 'PrimeiraParcela', header: '1ª Parcela', format: 'date', width: 14 },
          { key: 'Status', header: 'Status', width: 12 },
        ],
      },
      {
        name: 'Por status',
        rows: result.totais.porStatus.map((s) => ({
          Status: STATUS_LABELS[s.status as ValeStatus] ?? s.status,
          Count: s.count,
          Valor: s.valor,
        })),
        columns: [
          { key: 'Status', header: 'Status', width: 14 },
          { key: 'Count', header: 'Count', format: 'integer', width: 10 },
          { key: 'Valor', header: 'Valor', format: 'money', width: 14 },
        ],
      },
    ]);
  }

  return (
    <RelatorioPageShell
      title="Vales / Adiantamentos"
      subtitle="Solicitações no período, opcionalmente filtradas por colaborador."
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <PeriodoFilter
            dataInicio={periodo.dataInicio}
            dataFim={periodo.dataFim}
            onChange={setPeriodo}
            disabled={mutation.isPending}
          />
          <Box sx={{ minWidth: 260, flex: 1 }}>
            <ColaboradorSelect
              value={empregadoId}
              onChange={(id) => setEmpregadoId(id)}
              label="Colaborador (opcional)"
              disabled={mutation.isPending}
            />
          </Box>
        </Stack>
      }
      canGenerate={canGenerate}
      onGenerate={gerar}
      generating={mutation.isPending}
      hasResult={!!result}
      error={mutation.isError ? mutation.error : null}
      emptyMessage="Escolha o período e clique em Gerar."
      onExportPdf={exportPdf}
      onExportXlsx={exportXlsx}
      preview={result ? <Preview result={result} /> : null}
    />
  );
}

function Preview({ result }: { result: AdvancesDto }): JSX.Element {
  return (
    <>
      <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap">
        <ResumoItem label="Vales" value={String(result.totais.count)} />
        <ResumoItem label="Valor total" value={formatMoney(result.totais.totalValor)} />
      </Stack>

      {result.linhas.length === 0 ? (
        <Typography color="text.secondary">Nenhum vale no período.</Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Colaborador</TableCell>
                <TableCell>Solicitação</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="right">Parcelas</TableCell>
                <TableCell>1ª Parcela</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.linhas.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.colaboradorNome}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {formatDate(l.dataSolicitacao)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {formatMoney(l.valor)}
                  </TableCell>
                  <TableCell align="right">{l.nroParcelas}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {formatDate(l.vencimento1aParcela)}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_LABELS[l.status]} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {result.totais.porStatus.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}
          flexWrap="wrap"
        >
          {result.totais.porStatus.map((s) => (
            <Chip
              key={s.status}
              label={`${STATUS_LABELS[s.status as ValeStatus] ?? s.status}: ${s.count} · ${formatMoney(s.valor)}`}
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </>
  );
}

function ResumoItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  );
}
